import { useEffect, useState } from "react";
import { PlayCircle, Sparkles, FileSpreadsheet, CheckCircle2, AlertCircle, GraduationCap } from "lucide-react";
import Modal from "../../components/ui/Modal";
import { ApiService } from "../../services/api";
import type { TeachSession } from "../../types/navigator";

interface Props {
  sectionId?: string;
  gainId?: string;
  onClose: () => void;
  onStartTest: (testScope: { sectionId: string | null; count: number }, konu: string) => void;
}

const toSeconds = (t?: string | null): number | undefined => {
  if (!t) return undefined;
  const p = String(t).split(":").map((n) => parseInt(n, 10));
  if (p.some((n) => isNaN(n))) return undefined;
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return p[0];
};

const youtubeId = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

export function VideoEmbed({ url, startTime, endTime }: { url: string; startTime: string | null; endTime: string | null }) {
  const start = toSeconds(startTime);
  const end = toSeconds(endTime);
  const yt = youtubeId(url);

  if (yt) {
    const params = new URLSearchParams({ rel: "0", modestbranding: "1" });
    if (start != null) params.set("start", String(start));
    if (end != null) params.set("end", String(end));
    return (
      <div className="relative w-full overflow-hidden rounded-lg bg-black" style={{ paddingTop: "56.25%" }}>
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${yt}?${params.toString()}`}
          title="Ders videosu"
          frameBorder={0}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (/\.mp4($|\?)/i.test(url)) {
    return <video src={url} controls className="w-full rounded-lg bg-black" />;
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:text-blue-700">
      Videoyu aç →
    </a>
  );
}

export default function TeachFlow({ sectionId, gainId, onClose, onStartTest }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TeachSession | null>(null);
  const [videoDone, setVideoDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await ApiService.startTeach({ sectionId, gainId });
        if (alive) setData(res);
      } catch (e: any) {
        if (alive) setError(e?.message || "Ders hazırlanamadı");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [sectionId, gainId]);

  const markVideo = async () => {
    if (!data || videoDone) return;
    setVideoDone(true);
    try {
      await ApiService.recordTaught(data.kazanim.gainId, { video: true });
    } catch {
      /* sessizce geç — UI'ı bloklama */
    }
  };

  const startAI = async () => {
    if (!data) return;
    try {
      await ApiService.recordTaught(data.kazanim.gainId, { ai: true });
    } catch {
      /* geç */
    }
    // Mevcut AI öğretmen panelini serbest-konu context'iyle aç
    window.dispatchEvent(new CustomEvent("toggleAITutorPanel", { detail: { context: data.aiContext } }));
    onClose();
  };

  const startTest = async () => {
    if (!data) return;
    await markVideo();
    onStartTest(data.testScope, data.kazanim.konu);
    onClose();
  };

  const title = data ? `Ders · ${data.kazanim.konu}` : "Ders anlat";

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg" closeOnBackdrop={false}>
      {loading && (
        <div className="grid place-items-center py-16">
          <div className="h-9 w-9 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div className="space-y-4">
          {/* Kazanım başlığı */}
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-blue-700">{data.kazanim.unit}</p>
              <p className="text-sm font-semibold text-gray-900">{data.kazanim.gainText}</p>
            </div>
          </div>

          {/* Video — "senin için seçtim" */}
          {data.video?.url ? (
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <PlayCircle className="h-4 w-4 text-blue-600" />
                Senin için bu videoyu seçtim{data.video.startTime ? ` (${data.video.startTime}–${data.video.endTime})` : ""} — otur izle
              </div>
              <VideoEmbed url={data.video.url} startTime={data.video.startTime} endTime={data.video.endTime} />
              <button
                onClick={markVideo}
                disabled={videoDone}
                className={`mt-2 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                  videoDone
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <CheckCircle2 className="h-4 w-4" /> {videoDone ? "İzledim ✓" : "İzledim"}
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              Bu kazanım için hazır video yok — doğrudan AI öğretmenle çalışabilir ya da test çözebilirsin.
            </div>
          )}

          {/* Sonraki adımlar */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs text-gray-500">
            Çalışma yolu: <b className="text-gray-700">videoyu izle</b> → <b className="text-gray-700">AI öğretmenle pekiştir</b> →{" "}
            <b className="text-gray-700">mini test</b>. Her adım koçun defterine işlenir.
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              onClick={startAI}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Sparkles className="h-4 w-4" /> AI öğretmenle çalış
            </button>
            <button
              onClick={startTest}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-blue-300 hover:bg-gray-50"
            >
              <FileSpreadsheet className="h-4 w-4 text-blue-600" /> Mini test ({data.testScope.count} soru)
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
